import React, { useState, useEffect } from 'react';
import api, { API_BASE } from '../utils/api';
import './DogManagement.css';

const photoSrc = (url) => (url && (url.startsWith('http') || url.startsWith('data:')) ? url : `${API_BASE || ''}${url || ''}`) || '';

const getRiskColor = (score) => {
  if (score >= 70) return '#dc2626';
  if (score >= 50) return '#ea580c';
  if (score >= 30) return '#d97706';
  return '#16a34a';
};

// CSS-class-safe slug for status strings that may contain slashes or other
// non-alphanumerics (e.g. "On Rest / Season" -> "on-rest-season").
const statusClassSlug = (status) =>
  (status || 'active').replace(/[^a-z0-9]+/gi, '-').toLowerCase().replace(/^-+|-+$/g, '');

function DogEditModal({ dogName, currentUser, onClose, onSaved }) {
  const isViewer = currentUser?.role === 'viewer';
  const canEdit = !isViewer;

  const [selectedDog, setSelectedDog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    note: '',
    notes: [],
    photo_url: null,
    status: 'active',
    is_missing: false,
    photoFile: null,
    video_url: null
  });
  const [manualRaceForm, setManualRaceForm] = useState({
    date: '',
    track: '',
    position: '',
    comments: '',
    link_url: '',
    is_incident: false
  });
  const [showManualRaceForm, setShowManualRaceForm] = useState(false);
  const [incidentForm, setIncidentForm] = useState({ race_id: null, note: '', incident_id: null });
  const [savingIncident, setSavingIncident] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await api.get(`/api/dog-details/${encodeURIComponent(dogName)}`);
        if (cancelled) return;
        setSelectedDog({ ...response.data, is_pinned: response.data.is_pinned || false });
        setFormData({
          name: response.data.name,
          note: '',
          notes: response.data.notes || [],
          photo_url: response.data.metadata?.photo_url || null,
          status: response.data.metadata?.status || 'active',
          is_missing: response.data.metadata?.is_missing || false,
          photoFile: null,
          video_url: response.data.metadata?.video_url || null,
        });
      } catch (error) {
        console.error('Error fetching dog details:', error);
        alert('Failed to load dog details. Please try again.');
        onClose();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dogName]);

  const refreshSelectedDog = async () => {
    const response = await api.get(`/api/dog-details/${encodeURIComponent(dogName)}`);
    setSelectedDog(response.data);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, photoFile: file, photo_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = async () => {
    if (!canEdit) return;

    if (!window.confirm('Are you sure you want to remove this photo?')) {
      return;
    }

    try {
      await api.put(`/api/dogs/${encodeURIComponent(dogName)}/metadata`, {
        photo_url: null
      });

      // Update form data to reflect removal
      setFormData({ ...formData, photo_url: null, photoFile: null });

      // Refresh selected dog details
      await refreshSelectedDog();
    } catch (error) {
      console.error('Error removing photo:', error);
      alert('Failed to remove photo. Please try again.');
    }
  };

  const handleRemoveNote = async (noteId) => {
    if (!canEdit) return;

    if (!window.confirm('Are you sure you want to remove this note?')) {
      return;
    }

    try {
      await api.delete(`/api/dogs/${encodeURIComponent(dogName)}/notes/${noteId}`);

      // Refresh selected dog details to update notes list
      await refreshSelectedDog();

      // Also update formData notes if it exists
      if (formData.notes) {
        setFormData({
          ...formData,
          notes: formData.notes.filter(note => note.id !== noteId)
        });
      }
    } catch (error) {
      console.error('Error removing note:', error);
      alert('Failed to remove note. Please try again.');
    }
  };

  const handleAddManualRace = async (e) => {
    e.preventDefault();
    if (!canEdit) return;

    if (!manualRaceForm.date) {
      alert('Race date is required');
      return;
    }

    try {
      await api.post(`/api/dogs/${encodeURIComponent(dogName)}/manual-races`, manualRaceForm);

      // Refresh selected dog details to update races list
      await refreshSelectedDog();

      // Reset form
      setManualRaceForm({
        date: '',
        track: '',
        position: '',
        comments: '',
        link_url: '',
        is_incident: false
      });
      setShowManualRaceForm(false);
    } catch (error) {
      console.error('Error adding manual race:', error);
      alert(error.response?.data?.error || 'Failed to add race. Please try again.');
    }
  };

  const handleDeleteManualRace = async (raceId) => {
    if (!canEdit) return;

    if (!window.confirm('Are you sure you want to delete this manually added race?')) {
      return;
    }

    try {
      await api.delete(`/api/dogs/${encodeURIComponent(dogName)}/manual-races/${raceId}`);

      // Refresh selected dog details to update races list
      await refreshSelectedDog();
    } catch (error) {
      console.error('Error deleting manual race:', error);
      alert('Failed to delete race. Please try again.');
    }
  };

  const openIncidentForm = (race) => {
    setIncidentForm({
      race_id: race.race_id,
      note: race.incident_note || '',
      incident_id: null,  // backend upserts by (RaceID, DogID); we don't need a client-side id
    });
  };

  const cancelIncidentForm = () => {
    setIncidentForm({ race_id: null, note: '', incident_id: null });
  };

  const saveIncident = async (race) => {
    if (!race?.race_id) return;
    setSavingIncident(true);
    try {
      await api.post(`/api/races/${race.race_id}/incidents`, {
        dog_name: dogName,
        note: incidentForm.note,
      });
      await refreshSelectedDog();
      cancelIncidentForm();
    } catch (err) {
      alert(err?.response?.data?.error || 'Could not save incident');
    } finally {
      setSavingIncident(false);
    }
  };

  const deleteIncident = async (race) => {
    if (!race?.race_id) return;
    if (!window.confirm('Remove this incident annotation?')) return;
    try {
      // The dog-details payload gives us the incident note but not the incident id,
      // so look it up by (RaceID, dog_name). At most one match exists per the DB unique key.
      const listResp = await api.get(`/api/races/${race.race_id}/incidents`);
      const matching = (listResp.data?.incidents || []).find((inc) => inc.dog_name === dogName);
      if (!matching) return;
      await api.delete(`/api/races/${race.race_id}/incidents/${matching.id}`);
      await refreshSelectedDog();
    } catch (err) {
      alert(err?.response?.data?.error || 'Could not delete incident');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Upload photo if new file selected
      if (formData.photoFile) {
        const metadataFormData = new FormData();
        metadataFormData.append('photo', formData.photoFile);
        metadataFormData.append('user_id', 1);
        await api.post(`/api/dogs/${encodeURIComponent(dogName)}/photo`, metadataFormData);
      }

      // Always update metadata (status and missing flag)
      // Don't allow changing status or missing flag if dog is adopted
      const updateData = {
        user_id: 1
      };

      if (!selectedDog.adoption || !selectedDog.adoption.has_adoption_group) {
        updateData.status = formData.status;
        updateData.is_missing = formData.is_missing;
      } else {
        // If adopted, keep status as ADOPTED and is_missing as false
        updateData.status = 'ADOPTED';
        updateData.is_missing = false;
      }

      if ('video_url' in formData) {
        updateData.video_url = formData.video_url || null;
      }

      await api.put(`/api/dogs/${encodeURIComponent(dogName)}/metadata`, updateData);

      // Add note if provided
      if (formData.note && formData.note.trim()) {
        await api.post(`/api/dogs/${encodeURIComponent(dogName)}/notes`, {
          note: formData.note,
          user_id: 1
        });
      }

      if (onSaved) onSaved(selectedDog);
      onClose();
    } catch (error) {
      console.error('Error updating dog:', error);
      const message = error.response?.data?.error || error.message || 'Failed to update dog. Please try again.';
      alert(message);
    }
  };

  if (loading || !selectedDog) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ padding: 24 }}>
          Loading dog details…
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Dog Details: {selectedDog.name}</h3>
          <button
            className="close-button"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="dog-details-view">
          <div className="detail-section">
            <h4>Dog Information</h4>
            <p><strong>Name:</strong> {selectedDog.name}</p>
            {selectedDog.birth_country && (
              <p><strong>Born:</strong> {selectedDog.birth_country === 'ROI' || selectedDog.birth_country === 'Ireland' ? 'ROI' : selectedDog.birth_country === 'GB' || selectedDog.birth_country === 'Great Britain' ? 'GB' : selectedDog.birth_country}</p>
            )}
            {selectedDog.dog_profile_url && (
              <p>
                <strong>View Dog Profile:</strong>{' '}
                <a
                  href={selectedDog.dog_profile_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#2563eb', textDecoration: 'underline' }}
                >
                  View Racing Profile
                </a>
              </p>
            )}
          </div>

          {selectedDog.trainer && (
            <div className="detail-section">
              <h4>Trainer Information</h4>
              <p><strong>Name:</strong> {selectedDog.trainer.name}</p>
              <p><strong>Wins:</strong> {selectedDog.trainer.wins}</p>
              <p><strong>Runs:</strong> {selectedDog.trainer.runs}</p>
              <p><strong>Strike Rate:</strong> {selectedDog.trainer.strike_rate}</p>
              <p><strong>Age:</strong> {selectedDog.trainer.age} years</p>
              {selectedDog.trainer.url && (
                <p>
                  <strong>View Trainer:</strong>{' '}
                  <a
                    href={selectedDog.trainer.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#2563eb', textDecoration: 'underline' }}
                  >
                    View Trainer Profile
                  </a>
                </p>
              )}
            </div>
          )}

          {selectedDog.adoption && selectedDog.adoption.has_adoption_group && (
            <div className="detail-section" style={{ backgroundColor: '#dcfce7', padding: '12px', borderRadius: '4px', border: '1px solid #16a34a' }}>
              <h4 style={{ color: '#16a34a', marginTop: 0 }}>✓ Adopted</h4>
              <p><strong>Adoption Group:</strong> {selectedDog.adoption.adoption_group || 'Unknown'}</p>
              {selectedDog.adoption.lookup_url && (
                <p>
                  <strong>Adoption Details:</strong>{' '}
                  <a href={selectedDog.adoption.lookup_url} target="_blank" rel="noopener noreferrer" style={{ color: '#16a34a' }}>
                    View Details
                  </a>
                </p>
              )}
            </div>
          )}

          {selectedDog.risk_score !== undefined && selectedDog.risk_score > 0 && (
            <div className="detail-section">
              <h4>Risk Assessment</h4>
              <p><strong>Risk Score:</strong>
                <span style={{
                  color: getRiskColor(selectedDog.risk_score),
                  fontWeight: 'bold',
                  marginLeft: '10px'
                }}>
                  {selectedDog.risk_score}
                </span>
              </p>
              {selectedDog.risk_factors && selectedDog.risk_factors.length > 0 && (
                <div>
                  <strong>Risk Factors:</strong>
                  <ul>
                    {selectedDog.risk_factors.map((factor, idx) => (
                      <li key={idx}>{factor}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="detail-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <h4 style={{ margin: 0 }}>Race History</h4>
              {canEdit && !showManualRaceForm && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setManualRaceForm({ date: '', track: '', position: '', comments: '', link_url: '', is_incident: false });
                      setShowManualRaceForm(true);
                    }}
                    className="add-button"
                    style={{ flex: 1 }}
                  >
                    Add Manual Race
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setManualRaceForm({ date: new Date().toISOString().slice(0,10), track: '', position: '', comments: '', link_url: '', is_incident: true });
                      setShowManualRaceForm(true);
                    }}
                    className="add-button"
                    style={{ flex: 1, background: '#ea580c' }}
                  >
                    Report Incident
                  </button>
                </div>
              )}
              {canEdit && showManualRaceForm && (
                <button
                  type="button"
                  onClick={() => setShowManualRaceForm(false)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
            <p>Total Races: {selectedDog.total_races || (selectedDog.races ? selectedDog.races.length : 0)}</p>

            {canEdit && showManualRaceForm && (
              <div style={{
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
                padding: '16px',
                marginBottom: '16px',
                backgroundColor: '#f9fafb'
              }}>
                <h5 style={{ marginTop: 0, marginBottom: '12px' }}>Add Manual Race</h5>
                <form onSubmit={handleAddManualRace}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                        Date <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <input
                        type="date"
                        value={manualRaceForm.date}
                        onChange={(e) => setManualRaceForm({ ...manualRaceForm, date: e.target.value })}
                        required
                        style={{
                          width: '100%',
                          padding: '6px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                        Track
                      </label>
                      <input
                        type="text"
                        value={manualRaceForm.track}
                        onChange={(e) => setManualRaceForm({ ...manualRaceForm, track: e.target.value })}
                        placeholder="e.g. Shelbourne Park"
                        style={{
                          width: '100%',
                          padding: '6px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                        Position
                      </label>
                      <input
                        type="text"
                        value={manualRaceForm.position}
                        onChange={(e) => setManualRaceForm({ ...manualRaceForm, position: e.target.value })}
                        placeholder="e.g. 1, 2, 3"
                        style={{
                          width: '100%',
                          padding: '6px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                        Link URL
                      </label>
                      <input
                        type="url"
                        value={manualRaceForm.link_url}
                        onChange={(e) => setManualRaceForm({ ...manualRaceForm, link_url: e.target.value })}
                        placeholder="https://..."
                        style={{
                          width: '100%',
                          padding: '6px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
                      Comments
                    </label>
                    <textarea
                      value={manualRaceForm.comments}
                      onChange={(e) => setManualRaceForm({ ...manualRaceForm, comments: e.target.value })}
                      placeholder="e.g. Baulked, Disqualified, etc."
                      rows="2"
                      style={{
                        width: '100%',
                        padding: '6px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>
                  <div style={{ marginTop: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={manualRaceForm.is_incident}
                        onChange={(e) => setManualRaceForm({...manualRaceForm, is_incident: e.target.checked})}
                      />
                      Mark as Incident (fall, collision, DNF, etc.)
                    </label>
                  </div>
                  <button
                    type="submit"
                    style={{
                      marginTop: '12px',
                      padding: '8px 16px',
                      backgroundColor: '#16a34a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Add Race
                  </button>
                </form>
              </div>
            )}

            {selectedDog.races && selectedDog.races.length > 0 && (
              <div className="races-list" style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '4px', padding: '8px' }}>
                {selectedDog.races.map((race, idx) => (
                  <div
                    key={race.is_manual ? race.id : idx}
                    className="race-item"
                    style={{
                      padding: '8px',
                      marginBottom: '4px',
                      borderRadius: '4px',
                      backgroundColor: race.is_manual ? '#fef3c7' : (race.link_url ? '#f3f4f6' : 'transparent'),
                      cursor: race.link_url ? 'pointer' : 'default',
                      transition: 'background-color 0.2s',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                    onClick={() => {
                      if (race.link_url && !race.is_manual) {
                        window.open(race.link_url, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    onMouseEnter={(e) => {
                      if (race.link_url && !race.is_manual) {
                        e.currentTarget.style.backgroundColor = '#e5e7eb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (race.is_manual) {
                        e.currentTarget.style.backgroundColor = '#fef3c7';
                      } else if (race.link_url) {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      {race.is_manual && (
                        <span style={{
                          fontSize: '11px',
                          backgroundColor: '#f59e0b',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          marginRight: '6px'
                        }}>
                          MANUAL
                        </span>
                      )}
                      {race.race_type === 'trial' && (
                        <span style={{
                          fontSize: '11px',
                          backgroundColor: '#7c3aed',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          marginRight: '6px',
                          fontWeight: 600,
                          letterSpacing: '0.5px'
                        }}>
                          TRIAL{race.grade ? ` · ${race.grade}` : ''}
                        </span>
                      )}
                      {race.is_incident && (
                        <span className="race-incident-badge">INCIDENT</span>
                      )}
                      {!race.is_manual && race.race_id && (
                        <div className="race-incident-actions" onClick={(e) => e.stopPropagation()}>
                          {incidentForm.race_id === race.race_id ? (
                            <div className="race-incident-form">
                              <textarea
                                rows={2}
                                value={incidentForm.note}
                                onChange={(e) => setIncidentForm({ ...incidentForm, note: e.target.value })}
                                onClick={(e) => e.stopPropagation()}
                                placeholder="Describe the incident..."
                              />
                              <div className="race-incident-form-actions">
                                <button
                                  type="button"
                                  onClick={() => saveIncident(race)}
                                  disabled={savingIncident}
                                >
                                  {savingIncident ? 'Saving...' : 'Save'}
                                </button>
                                <button type="button" onClick={cancelIncidentForm}>Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {race.incident_note ? (
                                <>
                                  <span className="race-incident-note">{race.incident_note}</span>
                                  {canEdit && (
                                    <>
                                      <button type="button" className="race-incident-link" onClick={() => openIncidentForm(race)}>Edit</button>
                                      <button type="button" className="race-incident-link danger" onClick={() => deleteIncident(race)}>Remove</button>
                                    </>
                                  )}
                                </>
                              ) : (
                                canEdit && (
                                  <button type="button" className="race-incident-link" onClick={() => openIncidentForm(race)}>+ Incident</button>
                                )
                              )}
                            </>
                          )}
                        </div>
                      )}
                      <strong>{race.track || 'Unknown Track'}</strong> - {(() => {
                        if (!race.date) return 'N/A';
                        // Format date consistently - if it's ISO format (YYYY-MM-DD), format it nicely
                        if (race.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                          const dateObj = new Date(race.date + 'T00:00:00');
                          return dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
                        }
                        return race.date;
                      })()} - Position: {race.position || 'N/A'}
                      {race.comments && <span className="race-comments"> ({race.comments})</span>}
                      {race.link_url && !race.is_manual && (
                        <span style={{ marginLeft: '8px', color: '#2563eb', fontSize: '12px' }}>
                          🔗 Click to view race
                        </span>
                      )}
                      {race.link_url && race.is_manual && (
                        <span style={{ marginLeft: '8px' }}>
                          <a
                            href={race.link_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{ color: '#2563eb', fontSize: '12px', textDecoration: 'underline' }}
                          >
                            🔗 View link
                          </a>
                        </span>
                      )}
                    </div>
                    {canEdit && race.is_manual && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteManualRace(race.id);
                        }}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          marginLeft: '8px'
                        }}
                        title="Delete manual race"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="detail-section">
            <h4>Notes</h4>
            {selectedDog.notes && selectedDog.notes.length > 0 ? (
              <div className="notes-list">
                {selectedDog.notes.map((note) => (
                  <div key={note.id} className="note-item" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '12px',
                    padding: '8px',
                    marginBottom: '8px',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    backgroundColor: 'var(--bg-secondary)'
                  }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: '0 0 4px 0' }}>{note.note}</p>
                      <small style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                        {note.created_at ? new Date(note.created_at).toLocaleDateString('en-GB') : ''}
                      </small>
                    </div>
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => handleRemoveNote(note.id)}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          whiteSpace: 'nowrap'
                        }}
                        title="Remove note"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p>No notes yet.</p>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="dog-form">
          <div className="form-group">
            <label>Photo</label>
            {formData.photo_url && (
              <div style={{ marginBottom: '10px' }}>
                <img
                  src={photoSrc(formData.photo_url)}
                  alt="Dog photo"
                  style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '4px', marginBottom: '8px' }}
                />
                {canEdit && (
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Remove Photo
                  </button>
                )}
              </div>
            )}
            {canEdit && (
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
              />
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              {selectedDog.adoption && selectedDog.adoption.has_adoption_group ? (
                <div style={{ padding: '8px', backgroundColor: '#dcfce7', borderRadius: '4px', border: '1px solid #16a34a' }}>
                  <strong style={{ color: '#16a34a' }}>ADOPTED</strong>
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#166534' }}>
                    Status automatically set to ADOPTED (cannot be changed)
                  </p>
                </div>
              ) : (
                <select
                  value={formData.status}
                  onChange={(e) => {
                    const newStatus = e.target.value;
                    // Picking an off-wall status (ADOPTED, On Rest / Season,
                    // Absent Due to Injury) implies the dog drops off the
                    // public Missing Wall — flip the Missing flag too so the
                    // saved metadata is internally consistent and the UI
                    // shows the change immediately.
                    const offWall = newStatus === 'ADOPTED' || newStatus === 'On Rest / Season' || newStatus === 'Absent Due to Injury';
                    setFormData({
                      ...formData,
                      status: newStatus,
                      is_missing: offWall ? false : formData.is_missing,
                    });
                  }}
                >
                  <option value="active">Active</option>
                  <option value="missing">Missing</option>
                  <option value="retired">Retired</option>
                  <option value="kept in kennels">Kept in Kennels</option>
                  <option value="used for breeding">Used for Breeding</option>
                  <option value="given up for adoption">Given Up for Adoption</option>
                  <option value="inactive">Inactive</option>
                  <option value="archived">Archived</option>
                  <option value="deceased">Deceased</option>
                  <option value="killed">Killed</option>
                  <option value="fallen">Fallen</option>
                  <option value="ADOPTED">Adopted</option>
                  <option value="On Rest / Season">On Rest / Season</option>
                  <option value="Absent Due to Injury">Absent Due to Injury</option>
                </select>
              )}
            </div>
            <div className="form-group">
              <label>Missing Status</label>
              {selectedDog.adoption && selectedDog.adoption.has_adoption_group ? (
                <div style={{ padding: '8px', backgroundColor: '#dcfce7', borderRadius: '4px', border: '1px solid #16a34a' }}>
                  <strong style={{ color: '#16a34a' }}>Not Missing</strong>
                  <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#166534' }}>
                    Adopted dogs cannot be marked as missing
                  </p>
                </div>
              ) : (
                <select
                  value={formData.is_missing ? 'true' : 'false'}
                  onChange={(e) => setFormData({ ...formData, is_missing: e.target.value === 'true' })}
                >
                  <option value="false">Not Missing</option>
                  <option value="true">Missing</option>
                </select>
              )}
            </div>
            {formData.is_missing && canEdit && (
              <div className="form-group">
                <label>Pin to Top of Wall</label>
                <button
                  type="button"
                  onClick={async () => {
                    const newPinned = !selectedDog.is_pinned;
                    try {
                      await api.put('/api/dogs/pin', { dog_name: selectedDog.name, is_pinned: newPinned });
                      setSelectedDog(prev => ({ ...prev, is_pinned: newPinned }));
                    } catch { alert('Failed to update pin status'); }
                  }}
                  className={`pin-button ${selectedDog.is_pinned ? 'pinned' : ''}`}
                  style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                >
                  {selectedDog.is_pinned ? 'Unpin — Remove from Top of Wall' : 'Pin — Show at Top of Wall'}
                </button>
              </div>
            )}
          </div>

          {canEdit && (
            <div style={{ marginTop: '12px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>Race Clip URL</label>
              <input
                type="url"
                placeholder="YouTube or direct video URL"
                value={formData.video_url || ''}
                onChange={(e) => setFormData({...formData, video_url: e.target.value})}
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
          )}

          <div className="form-group">
            <label>Add Note (optional)</label>
            <textarea
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              rows="4"
              placeholder="Add a note about this dog..."
            />
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="cancel-button"
            >
              Close
            </button>
            <button type="submit" className="submit-button">
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default DogEditModal;
export { statusClassSlug };
